import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Check, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Reveal } from '@/components/Reveal';
import { PaymentStep } from '@/components/PaymentStep';
import {
  PACKAGES,
  ADD_ONS,
  VEHICLE_TYPES,
  TIME_SLOTS,
  CONTACT_INFO,
  SPECIAL_PACKAGES,
  isPromoActive,
} from '@/lib/data';
import { createBooking, getQuote, api } from '@/lib/api';
import { LUXE } from '@/constants/testIds';

const useQueryParam = (name) => {
  const { search } = useLocation();
  return new URLSearchParams(search).get(name);
};

const BookingPage = () => {
  const preselected = useQueryParam('package');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    vehicle_type: 'Sedan',
    service: preselected || PACKAGES[0].name,
    add_ons: [],
    preferred_date: '',
    preferred_time: TIME_SLOTS[0],
    location: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);
  const [quote, setQuote] = useState(null);

  // Refresh live quote whenever service/vehicle/add-ons change
  useEffect(() => {
    let cancelled = false;
    getQuote({
      service: form.service,
      vehicle_type: form.vehicle_type,
      add_ons: form.add_ons,
    }).then(
      (q) => {
        if (!cancelled) setQuote(q);
      },
      () => {}
    );
    return () => {
      cancelled = true;
    };
  }, [form.service, form.vehicle_type, form.add_ons]);

  useEffect(() => {
    if (preselected) {
      setForm((f) => ({ ...f, service: preselected }));
    }
  }, [preselected]);

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const toggleAddon = (name) =>
    setForm((f) => ({
      ...f,
      add_ons: f.add_ons.includes(name)
        ? f.add_ons.filter((a) => a !== name)
        : [...f.add_ons, name],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.preferred_date) {
      toast.error('Please fill in your name, phone, and preferred date.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.location) delete payload.location;
      if (!payload.notes) delete payload.notes;
      const booking = await createBooking(payload);
      setCreatedBooking(booking);
      setSubmitted(true);
      toast.success('Details saved — complete your deposit to submit.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail || 'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="pt-28">
      <section className="py-16 sm:py-24">
        <div className="luxe-container grid lg:grid-cols-12 gap-14">
          {/* Left column: heading + info */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="luxe-eyebrow mb-4">Book Your Detail</div>
              <h1 className="text-4xl sm:text-5xl text-white leading-[1.05]">
                Schedule Your <br />
                <span className="italic text-[#C9A24A]">Luxury</span> Experience.
              </h1>
              <div className="gold-divider mt-8" />
              <p className="mt-8 text-white/60 leading-relaxed">
                Fill out the form and we'll reach out shortly to confirm your
                appointment. Mobile service across St. Louis and surrounding
                areas.
              </p>

              <div className="mt-10 space-y-6 text-sm">
                {[
                  'Insured & professional service',
                  'Mobile — we come to you',
                  'Weekend availability',
                  'Satisfaction guaranteed',
                ].map((b) => (
                  <div key={b} className="flex items-start gap-3 text-white/80">
                    <Check size={16} className="text-[#C9A24A] mt-1" /> {b}
                  </div>
                ))}
              </div>

              <div className="mt-12 p-6 border border-[#C9A24A]/20 bg-[#131A2D]">
                <div className="luxe-eyebrow mb-3">Prefer to Call?</div>
                <a
                  href={`tel:${CONTACT_INFO.phoneRaw}`}
                  data-testid="booking-call-link"
                  className="text-2xl text-white hover:text-[#C9A24A] transition-colors font-serif"
                >
                  {CONTACT_INFO.phone}
                </a>
              </div>
            </Reveal>
          </div>

          {/* Right column: form */}
          <div className="lg:col-span-7">
            {submitted ? (
              <div className="space-y-6">
                <div
                  data-testid={LUXE.bookingSuccess}
                  className="luxe-card p-8 text-center border-[#C9A24A]/60"
                >
                  <CheckCircle2 size={40} className="text-[#C9A24A] mx-auto mb-4" />
                  <h2 className="text-2xl text-white mb-2 font-serif">Almost there</h2>
                  <div className="gold-divider mx-auto mb-4" />
                  <p className="text-white/70 max-w-md mx-auto text-sm">
                    Your details look great. Complete the required deposit
                    below to officially submit your booking request.
                  </p>
                </div>

                {createdBooking && (
                  <PaymentStep booking={createdBooking} pricing={quote} />
                )}

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false);
                      setCreatedBooking(null);
                      setForm({
                        name: '',
                        phone: '',
                        email: '',
                        vehicle_type: 'Sedan',
                        service: PACKAGES[0].name,
                        add_ons: [],
                        preferred_date: '',
                        preferred_time: TIME_SLOTS[0],
                        location: '',
                        notes: '',
                      });
                    }}
                    data-testid="booking-new-btn"
                    className="text-xs uppercase tracking-[0.24em] text-white/50 hover:text-[#C9A24A] transition-colors"
                  >
                    ← Edit details
                  </button>
                </div>
              </div>
            ) : (
              <form
                data-testid={LUXE.bookingForm}
                onSubmit={handleSubmit}
                className="luxe-card p-8 sm:p-10 space-y-6"
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="luxe-eyebrow block mb-2">Full Name</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      data-testid={LUXE.bookingName}
                      onChange={(e) => setField('name', e.target.value)}
                      placeholder="Your name"
                      className="luxe-input"
                    />
                  </div>
                  <div>
                    <label className="luxe-eyebrow block mb-2">Phone Number</label>
                    <input
                      type="tel"
                      required
                      value={form.phone}
                      data-testid={LUXE.bookingPhone}
                      onChange={(e) => setField('phone', e.target.value)}
                      placeholder="(555) 555-5555"
                      className="luxe-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="luxe-eyebrow block mb-2">Email (optional)</label>
                  <input
                    type="email"
                    value={form.email}
                    data-testid={LUXE.bookingEmail}
                    onChange={(e) => setField('email', e.target.value)}
                    placeholder="you@example.com"
                    className="luxe-input"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="luxe-eyebrow block mb-2">Vehicle Type</label>
                    <select
                      value={form.vehicle_type}
                      data-testid={LUXE.bookingVehicleType}
                      onChange={(e) => setField('vehicle_type', e.target.value)}
                      className="luxe-input appearance-none cursor-pointer"
                    >
                      {VEHICLE_TYPES.map((v) => (
                        <option key={v} value={v} className="bg-[#131A2D]">
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="luxe-eyebrow block mb-2">Service</label>
                    <select
                      value={form.service}
                      data-testid={LUXE.bookingService}
                      onChange={(e) => setField('service', e.target.value)}
                      className="luxe-input appearance-none cursor-pointer"
                    >
                      {isPromoActive() && (
                        <optgroup label="🎆 4th of July Specials (7/5 – 7/12)">
                          {SPECIAL_PACKAGES.map((p) => (
                            <option key={p.id} value={p.name} className="bg-[#131A2D]">
                              {p.displayName} — ${p.priceSedan}/${p.priceSuv}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label="Signature Packages">
                        {PACKAGES.map((p) => (
                          <option key={p.id} value={p.name} className="bg-[#131A2D]">
                            {p.name} — ${p.priceSedan}/${p.priceSuv}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="luxe-eyebrow block mb-3">Add-Ons</label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {ADD_ONS.map((a) => {
                      const active = form.add_ons.includes(a.name);
                      return (
                        <button
                          key={a.id}
                          type="button"
                          data-testid={`booking-${a.testId}`}
                          onClick={() => toggleAddon(a.name)}
                          className={`flex items-center justify-between px-4 py-3 border text-left transition-all text-sm ${
                            active
                              ? 'border-[#C9A24A] bg-[#C9A24A]/10 text-white'
                              : 'border-[#C9A24A]/20 text-white/70 hover:border-[#C9A24A]/50'
                          }`}
                        >
                          <span className="flex items-center gap-3">
                            <span
                              className={`w-4 h-4 border flex items-center justify-center ${
                                active
                                  ? 'bg-[#C9A24A] border-[#C9A24A]'
                                  : 'border-[#C9A24A]/40'
                              }`}
                            >
                              {active && (
                                <Check size={10} className="text-[#0B0F1A]" />
                              )}
                            </span>
                            {a.name}
                          </span>
                          <span className="text-[#C9A24A] text-xs">{a.price}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="luxe-eyebrow block mb-2">Preferred Date</label>
                    <input
                      type="date"
                      required
                      min={today}
                      value={form.preferred_date}
                      data-testid={LUXE.bookingDate}
                      onChange={(e) => setField('preferred_date', e.target.value)}
                      className="luxe-input"
                      style={{ colorScheme: 'dark' }}
                    />
                  </div>
                  <div>
                    <label className="luxe-eyebrow block mb-2">Preferred Time</label>
                    <select
                      value={form.preferred_time}
                      data-testid={LUXE.bookingTime}
                      onChange={(e) => setField('preferred_time', e.target.value)}
                      className="luxe-input appearance-none cursor-pointer"
                    >
                      {TIME_SLOTS.map((t) => (
                        <option key={t} value={t} className="bg-[#131A2D]">
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="luxe-eyebrow block mb-2">
                    Location (if mobile service)
                  </label>
                  <input
                    type="text"
                    value={form.location}
                    data-testid={LUXE.bookingLocation}
                    onChange={(e) => setField('location', e.target.value)}
                    placeholder="Street address, city"
                    className="luxe-input"
                  />
                </div>

                <div>
                  <label className="luxe-eyebrow block mb-2">Notes (optional)</label>
                  <textarea
                    rows={4}
                    value={form.notes}
                    data-testid={LUXE.bookingNotes}
                    onChange={(e) => setField('notes', e.target.value)}
                    placeholder="Anything we should know about your vehicle?"
                    className="luxe-input resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid={LUXE.bookingSubmit}
                  className="btn-gold w-full disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Saving...
                    </>
                  ) : (
                    <>
                      Continue to Deposit <ArrowRight size={14} />
                    </>
                  )}
                </button>

                <p className="text-white/40 text-xs text-center">
                  Next: place a 50% deposit to officially submit your booking.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
